/**
 * WGChairDetails Component - Working Group Chair and Vice Chair Display - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Display-only working group interface featuring chair/vice chair profiles,
 * update viewing with history navigation, and member profile modals. Shows
 * working group updates in a read-only format with update history selection.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.currentGroup - Current working group data object
 * @param {string} props.currentGroup.workingGroupName - Name of the working group
 * @returns {JSX.Element} The WGChairDetails component
 * 
 * @example
 * <WGChairDetails currentGroup={{workingGroupName: "Policy Working Group"}} />
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ProfileModal from '../../modals/ProfileModal';
import apiHelper from '../../utils/apiHelper';
import { FILE_NAMES } from '../../utils/constants';
import Parser from 'html-react-parser';
import './WGChairDetails.css';

/**
 * Generates initials from first and last names
 * 
 * @param {string} firstName - First name
 * @param {string} lastName - Last name
 * @returns {string} Uppercase initials (e.g., "JD" for John Doe)
 */
function getInitials(firstName, lastName) {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
}

function WGChairDetails({ currentGroup }) {
  const { id } = useParams();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [periodicUpdate, setPeriodicUpdate] = useState(null);
  const [groupMembers, setGroupMembers] = useState({ chair: null, viceChair: null });
  const [updates, setUpdates] = useState([]);
  const [currentUpdateIndex, setCurrentUpdateIndex] = useState(0);

  const [chairImage, setChairImage] = useState(null);
  const [viceChairImage, setViceChairImage] = useState(null);



  // Fetch working group data and updates
  useEffect(() => {
    /**
     * Fetches working group member details and updates from API
     * Filters and sorts updates by date, handles validation for required fields
     */
    const fetchData = async () => {
      try {
        const memberResponse = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.WG_MEMBER_DETAILS);

        if (memberResponse && Array.isArray(memberResponse.fileContent)) {
          const groupName = currentGroup.workingGroupName;
          
          const members = memberResponse.fileContent.filter(member => 
            member.workingGroupName === groupName
          );
          
          const chairMember = members.find(m => m.workingGroupPosition === "Chair");
          const viceChairMember = members.find(m => m.workingGroupPosition === "Vice Chair");

          setGroupMembers({
            chair: chairMember || null,
            viceChair: viceChairMember || null
          });
        }

        // Then fetch updates...
        const response = await fetch(`${process.env.REACT_APP_API_URL}graph/getWorkingGroupUpdates/${id}`, {
          headers: {
            'Authorization': `Bearer ${await apiHelper.getAuthToken()}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch updates');
        }

        const fetchedUpdates = await response.json();
        
        // Filter out invalid updates (where required fields are undefined)
        const validUpdates = fetchedUpdates.filter(update => 
          update.updateDate && 
          update.update && 
          update.updatedBy
        );

        if (validUpdates.length > 0) {
          const sortedUpdates = validUpdates.sort((a, b) => 
            new Date(b.updateDate) - new Date(a.updateDate)
          );
          setUpdates(sortedUpdates);
          setPeriodicUpdate(sortedUpdates[0]);
        } else {
          setUpdates([]);
          setPeriodicUpdate(null);
        }
      } catch (error) {
        setUpdates([]);
        setPeriodicUpdate(null);
        console.error('Failed to load updates:', error);
      }
    };

    fetchData();
  }, [currentGroup.workingGroupName, id]);

  // Load chair profile image
  useEffect(() => {
    if (groupMembers.chair?.email) {
        /**
         * Loads chair profile image from server with authenticated request
         * Creates blob URL for image display and handles errors gracefully
         */
        const loadImage = async () => {
            try {
                // First get the profile
                const profile = await apiHelper.getMemberProfileByEmail(groupMembers.chair.email);
                if (!profile?.photo) {
                    setChairImage(null);
                    return;
                }

                // Get the image using the photo filename
                const authToken = await apiHelper.getAuthToken();
                const response = await fetch(`${process.env.REACT_APP_API_URL}graph/getMediaContent/profile-images/${profile.photo}`, {
                    headers: {
                        'Authorization': `Bearer ${authToken}`
                    }
                });

                if (!response.ok) {
                    setChairImage(null);
                    return;
                }

                const blob = await response.blob();
                const imageUrl = URL.createObjectURL(blob);
                setChairImage(imageUrl);
            } catch (error) {
                setChairImage(null);
            }
        };
        loadImage();
    }

    return () => {
        if (chairImage?.startsWith('blob:')) {
            URL.revokeObjectURL(chairImage);
        }
    };
  }, [groupMembers.chair?.email]);

  // Load vice chair profile image
  useEffect(() => {
    if (groupMembers.viceChair?.email) {
        /**
         * Loads vice chair profile image from server with authenticated request
         * Creates blob URL for image display and handles errors gracefully
         */
        const loadImage = async () => {
            try {
                const profile = await apiHelper.getMemberProfileByEmail(groupMembers.viceChair.email);
                if (!profile?.photo) {
                    setViceChairImage(null);
                    return;
                }

                const authToken = await apiHelper.getAuthToken();
                const response = await fetch(`${process.env.REACT_APP_API_URL}graph/getMediaContent/profile-images/${profile.photo}`, {
                    headers: {
                        'Authorization': `Bearer ${authToken}`
                    }
                });

                if (!response.ok) {
                    setViceChairImage(null);
                    return;
                }

                const blob = await response.blob();
                const imageUrl = URL.createObjectURL(blob);
                setViceChairImage(imageUrl);
            } catch (error) {
                setViceChairImage(null);
            }
        };
        loadImage();
    }

    return () => {
        if (viceChairImage?.startsWith('blob:')) {
            URL.revokeObjectURL(viceChairImage);
        }
    };
  }, [groupMembers.viceChair?.email]);

  /**
   * Handles profile card click to open member profile modal
   * 
   * @param {Object} member - Member object containing profile information
   * @param {string|number} member.id - Unique member identifier
   */
  const handleProfileClick = (member) => {
    setProfileData({id: member.id });
    setShowProfileModal(true);
  }





   return (
    <>
      <div className="wg-chair-container">
        {/* Chair Section - Always show if exists */}
        {groupMembers.chair && (
          <div className="wg-member-card" onClick={() => handleProfileClick(groupMembers.chair)}>
            {chairImage ? (
              <img
                src={chairImage}
                alt="Chair"
                className="wg-member-image"
              />
            ) : (
              <div className="wg-member-initials">
                {getInitials(groupMembers.chair.firstName, groupMembers.chair.lastName)}
              </div>
            )}
            <h4 className="wg-member-name">
              {`${groupMembers.chair.firstName} ${groupMembers.chair.lastName}`}
            </h4>
            <p className="wg-member-role">Chair</p>
          </div>
        )}

        {/* Updates Section */}
        <div className="wg-updates-section">
          <div className="monthly-update">
            <div className="update-header">
              <h3>
                Update: {currentGroup.workingGroupName}
                {periodicUpdate?.updateDate && (
                  <span className="update-date">
                    {" - "}
                    {new Date(periodicUpdate.updateDate).toLocaleString('default', { 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </span>
                )}
              </h3>
              {updates?.length > 1 && periodicUpdate && (
                <select 
                  className="update-selector"
                  value={currentUpdateIndex}
                  onChange={(e) => {
                    const newIndex = parseInt(e.target.value);
                    setCurrentUpdateIndex(newIndex);
                    setPeriodicUpdate(updates[newIndex]);
                  }}
                >
                  {updates.map((update, index) => (
                    <option key={index} value={index}>
                      {new Date(update.updateDate).toLocaleString('default', { 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="update-display">
              {periodicUpdate?.update ? (
                <div className="update-content">
                  {Parser(periodicUpdate.update)}
                </div>
              ) : (
                <div className="no-updates">
                  <p>No recent updates available for this working group.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Vice Chair Section - Always show if exists */}
        {groupMembers.viceChair && (
          <div className="wg-member-card" onClick={() => handleProfileClick(groupMembers.viceChair)}>
            {viceChairImage ? (
              <img
                src={viceChairImage}
                alt="Vice Chair"
                className="wg-member-image"
              />
            ) : (
              <div className="wg-member-initials">
                {getInitials(groupMembers.viceChair.firstName, groupMembers.viceChair.lastName)}
              </div>
            )}
            <h4 className="wg-member-name">
              {`${groupMembers.viceChair.firstName} ${groupMembers.viceChair.lastName}`}
            </h4>
            <p className="wg-member-role">Vice Chair</p>
          </div>
        )}
      </div>

      {showProfileModal && (
        <ProfileModal
          show={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          small={true}
          profileData={profileData}
        />
      )}
    </>
  );
}

export default WGChairDetails;
